import type { CostEntry, Subscription } from '@orgus/protocol';
import { COST_CATEGORY_LABEL, formatUsd } from '@orgus/protocol';
import { CircleAlert, Cpu, FileUp, MoreHorizontal, Paperclip, Pencil, Plus, Receipt, Repeat, Trash2 } from '@orgus/ui/icons';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Panel,
  Segmented,
  Skeleton,
  SkeletonRows,
  Tooltip,
  formatBytes,
  plural,
  toUiError,
  toast,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { PageActions } from '../../components/shell/TopBar.tsx';
import { useMachines, useProjects } from '../../lib/store.ts';
import { EntryDialog } from './EntryDialog.tsx';
import { ImportDialog } from './ImportDialog.tsx';
import { CATEGORY_COLOR, dayRangeText, dayText } from './model.ts';
import { ScopeBar, useScope } from './Scope.tsx';
import { SubscriptionDialog } from './SubscriptionDialog.tsx';
import { SyncRow } from './SyncRow.tsx';
import { RECEIPT_ACCEPT, deleteEntry, deleteSubscription, receiptUrl, uploadReceipt, useFinance } from './useFinance.ts';

const SOURCE_LABEL: Record<CostEntry['source'], string> = { manual: 'Entered', invoice: 'Invoice', github: 'GitHub pull' };

/** 'Oct 14', 'Oct 1 to 31', through the shared date formatter. */
function dates(e: Pick<CostEntry, 'from' | 'to'>): string {
  return dayRangeText(e.from, e.to);
}

interface RowAction {
  label: string;
  /** The menu item's text on a phone, where the actions fold into one menu. */
  short: string;
  icon: ReactNode;
  danger?: boolean;
  onSelect(): void;
}

/**
 * A row's actions: icon buttons on hover from md up; on a phone, where nothing hovers and three
 * buttons would take the title's room, one More menu.
 */
function RowActions({ actions, narrow, name }: { actions: RowAction[]; narrow: boolean; name: string }) {
  if (narrow) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton label={`More actions for ${name}`} icon={<MoreHorizontal />} size="xs" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((a) => (
            <DropdownMenuItem key={a.short} icon={a.icon} danger={a.danger} onSelect={a.onSelect}>
              {a.short}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <span className="flex items-center gap-0.5">
      {actions.map((a) => (
        <IconButton key={a.short} label={a.label} icon={a.icon} size="xs" variant={a.danger ? 'danger' : undefined} onClick={a.onSelect} />
      ))}
    </span>
  );
}

function CategoryDot({ category }: { category: CostEntry['category'] }) {
  return <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: CATEGORY_COLOR[category] }} />;
}

export function LedgerSkeleton() {
  return (
    <div className="flex flex-col gap-5" role="status" aria-label="Loading the ledger">
      <Skeleton className="h-[58px]" />
      <div className="rounded-lg border border-border bg-surface">
        <SkeletonRows rows={6} />
      </div>
      <div className="rounded-lg border border-border bg-surface">
        <SkeletonRows rows={3} />
      </div>
    </div>
  );
}

/** Every cost that is not tokens: entries, invoices, CI pulls, seats and subscriptions. */
export function LedgerView() {
  const finance = useFinance();
  const scope = useScope();
  const projects = useProjects();
  const machines = useMachines();
  const narrow = useMediaQuery(NARROW_QUERY);
  const [entryDialog, setEntryDialog] = useState<{ open: boolean; entry: CostEntry | null }>({ open: false, entry: null });
  const [subDialog, setSubDialog] = useState<{ open: boolean; sub: Subscription | null }>({ open: false, sub: null });
  const [importOpen, setImportOpen] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'entry'; e: CostEntry } | { kind: 'sub'; s: Subscription } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [attachTo, setAttachTo] = useState<CostEntry | null>(null);
  const [source, setSource] = useState<'entered' | 'github' | 'all'>('entered');
  // A receipt that did not attach, said over the costs: errors never go in a toast.
  const [attachError, setAttachError] = useState<unknown>(null);

  if (!finance) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState
          size="page"
          icon={<Receipt />}
          title="This hub does not keep a finance ledger"
          description="Update Orgus on the hub machine and restart it."
        />
      </div>
    );
  }

  const projectName = (id: string | null) => (id ? (projects.find((p) => p.id === id)?.name ?? id) : 'Shared');
  const inScope = <T extends { projectId: string | null }>(x: T) => scope.projectId === null || x.projectId === scope.projectId || x.projectId === null;
  const scoped = finance.entries.filter(inScope);
  const ciCount = scoped.filter((e) => e.source === 'github').length;
  const entries = scoped.filter((e) => (source === 'all' ? true : source === 'github' ? e.source === 'github' : e.source !== 'github'));
  const subs = finance.subscriptions.filter(inScope);
  const rated = finance.settings.computeRates.filter((r) => r.hourlyRate > 0);

  const attach = async (e: CostEntry, f: File | undefined) => {
    if (!f) return;
    setAttachError(null);
    try {
      await uploadReceipt(e, f);
      toast(`Receipt attached to ${e.vendor}`, { tone: 'success' });
    } catch (err) {
      setAttachError(toUiError(err, { what: 'That receipt was not attached.' }));
    }
  };

  const entryColumns: Column<CostEntry>[] = [
    { key: 'date', header: 'Dates', hideBelow: 'md', text: (e) => dates(e), sortValue: (e) => e.from, cell: (e) => <span className="whitespace-nowrap text-fg-muted">{dates(e)}</span> },
    {
      key: 'vendor',
      header: 'Cost',
      grow: true,
      text: (e) => `${e.vendor} ${e.description} ${e.invoiceNumber ?? ''}`,
      cell: (e) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-fg">{e.vendor}</span>
          {/* On a phone the caption is the short date and the category; the description returns from md up. */}
          <span className="flex min-w-0 items-center gap-1.5 text-caption text-fg-subtle md:hidden">
            <CategoryDot category={e.category} />
            <span className="truncate">
              {dates(e)}, {COST_CATEGORY_LABEL[e.category]}
            </span>
          </span>
          <span className="hidden truncate text-caption text-fg-subtle md:block">
            {e.description || 'No description'}
            {e.invoiceNumber ? `, invoice ${e.invoiceNumber}` : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      hideBelow: 'md',
      text: (e) => COST_CATEGORY_LABEL[e.category],
      cell: (e) => (
        <span className="flex items-center gap-1.5 whitespace-nowrap text-fg-muted">
          <CategoryDot category={e.category} />
          {COST_CATEGORY_LABEL[e.category]}
        </span>
      ),
    },
    { key: 'project', header: 'Project', hideBelow: 'lg', text: (e) => projectName(e.projectId), cell: (e) => <span className="truncate text-fg-muted">{projectName(e.projectId)}</span> },
    { key: 'source', header: 'Source', hideBelow: 'lg', text: (e) => SOURCE_LABEL[e.source], cell: (e) => <span className="text-fg-muted">{SOURCE_LABEL[e.source]}</span> },
    {
      key: 'receipt',
      header: 'Receipt',
      hideBelow: 'lg',
      text: (e) => e.receipt?.name ?? '',
      cell: (e) =>
        e.receipt ? (
          <Tooltip content={`${e.receipt.name}, ${formatBytes(e.receipt.size)}`}>
            <a href={receiptUrl(e.id, e.receipt.type === 'application/pdf')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-fg underline-offset-2 hover:underline">
              <Paperclip className="size-3.5" aria-hidden />
              View
            </a>
          </Tooltip>
        ) : (
          // No receipt: nothing at rest (Attach is a row action), never a word that reads as data.
          <span className="sr-only">No receipt</span>
        ),
    },
    { key: 'amount', header: 'Amount', type: 'number', sortValue: (e) => e.amount, cell: (e) => <span className="whitespace-nowrap font-medium text-fg">{formatUsd(e.amount)}</span> },
  ];

  const monthly = (s: Subscription) => (s.seats * s.pricePerSeat) / (s.every === 'year' ? 12 : 1);
  const subColumns: Column<Subscription>[] = [
    {
      key: 'plan',
      header: 'Subscription',
      grow: true,
      text: (s) => `${s.vendor} ${s.plan}`,
      // On a phone the row is two lines that wrap rather than cut: the name with its monthly
      // amount on the right, then the seats; the chip folds into the caption.
      cell: (s) =>
        narrow ? (
          <span className="flex min-w-0 flex-col">
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 break-words text-fg">
                {s.vendor} {s.plan}
              </span>
              <span className="shrink-0 font-medium text-fg num">{formatUsd(monthly(s))}</span>
            </span>
            <span className="break-words text-caption text-fg-subtle">
              {plural(s.seats, 'seat')} at {formatUsd(s.pricePerSeat)} a {s.every}
              {s.agentSeat && ', agent seats'}
            </span>
          </span>
        ) : (
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-fg">
                {s.vendor} {s.plan}
              </span>
              <span className="truncate text-caption text-fg-subtle">
                {plural(s.seats, 'seat')} at {formatUsd(s.pricePerSeat)} a {s.every}
              </span>
            </span>
            {s.agentSeat && (
              <span className="shrink-0">
                <Badge tone="accent">Agent seats</Badge>
              </span>
            )}
          </span>
        ),
    },
    { key: 'project', header: 'Project', hideBelow: 'md', text: (s) => projectName(s.projectId), cell: (s) => <span className="text-fg-muted">{projectName(s.projectId)}</span> },
    {
      key: 'period',
      header: 'Runs',
      hideBelow: 'md',
      text: (s) => `${s.start} ${s.end ?? ''}`,
      sortValue: (s) => s.start,
      cell: (s) => <span className="whitespace-nowrap text-fg-muted">{s.end ? dayRangeText(s.start, s.end) : `Since ${dayText(s.start)}`}</span>,
    },
    ...(narrow
      ? []
      : [{ key: 'monthly', header: 'Per month', type: 'number' as const, sortValue: monthly, cell: (s: Subscription) => <span className="whitespace-nowrap font-medium text-fg">{formatUsd(monthly(s))}</span> }]),
  ];

  return (
    <>
      <PageActions>
        <Button size="sm" variant="secondary" icon={<FileUp />} onClick={() => setImportOpen(true)}>
          Import invoices
        </Button>
        <Button size="sm" variant="secondary" icon={<Repeat />} onClick={() => setSubDialog({ open: true, sub: null })}>
          Add subscription
        </Button>
        <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEntryDialog({ open: true, entry: null })}>
          Add cost
        </Button>
      </PageActions>
      <ScopeBar scope={scope} hideRange hideAccount updatedAt={finance.updatedAt} note={scope.projectId ? 'Shared costs included' : undefined} />
      <div className="flex flex-col gap-5">
        <SyncRow status={finance.sync.github} />
        {attachError != null && <ErrorState variant="inline" error={attachError} />}
        <section aria-label="Costs" className="flex flex-col gap-2">
          <h2 className="text-base font-medium text-fg">
            Costs <span className="text-caption font-normal text-fg-subtle">{entries.length ? `${plural(entries.length, 'entry', 'entries')}, ${formatUsd(entries.reduce((a, e) => a + e.amount, 0))}` : ''}</span>
          </h2>
          <DataTable
            label="Cost entries"
            rows={entries}
            columns={entryColumns}
            rowKey={(e) => e.id}
            searchable={{ placeholder: 'Search costs' }}
            filters={
              <Segmented<'entered' | 'github' | 'all'>
                label="Which costs"
                options={[
                  { value: 'entered', label: 'Entered', count: scoped.length - ciCount },
                  { value: 'github', label: 'GitHub CI', count: ciCount },
                  { value: 'all', label: 'All' },
                ]}
                value={source}
                onChange={setSource}
              />
            }
            pageSize={15}
            initialSort={{ key: 'date', dir: 'desc' }}
            rowActions={(e) =>
              e.source === 'github' ? null : (
                <RowActions
                  narrow={narrow}
                  name={e.vendor}
                  actions={[
                    {
                      label: e.receipt ? `Replace receipt of ${e.vendor}` : `Attach a receipt to ${e.vendor}`,
                      short: e.receipt ? 'Replace receipt' : 'Attach receipt',
                      icon: <Paperclip />,
                      onSelect: () => {
                        setAttachTo(e);
                        fileRef.current?.click();
                      },
                    },
                    { label: `Edit ${e.vendor}`, short: 'Edit cost', icon: <Pencil />, onSelect: () => setEntryDialog({ open: true, entry: e }) },
                    { label: `Delete ${e.vendor}`, short: 'Delete cost', icon: <Trash2 />, danger: true, onSelect: () => setConfirm({ kind: 'entry', e }) },
                  ]}
                />
              )
            }
            empty={
              <EmptyState
                icon={<Receipt />}
                title="No costs besides tokens yet"
                description="Add hosting, invoices and one-off spend to see each project's full cost."
                action={
                  <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEntryDialog({ open: true, entry: null })}>
                    Add cost
                  </Button>
                }
              />
            }
          />
        </section>
        <section aria-label="Seats and subscriptions" className="flex flex-col gap-2">
          <h2 className="text-base font-medium text-fg">
            Seats and subscriptions{' '}
            <span className="text-caption font-normal text-fg-subtle">
              {subs.length ? `${formatUsd(subs.filter((s) => !s.end).reduce((a, s) => a + (s.seats * s.pricePerSeat) / (s.every === 'year' ? 12 : 1), 0))} a month` : ''}
            </span>
          </h2>
          <DataTable
            label="Seats and subscriptions"
            rows={narrow ? [...subs].sort((a, b) => monthly(b) - monthly(a)) : subs}
            columns={subColumns}
            rowKey={(s) => s.id}
            pageSize={false}
            initialSort={narrow ? undefined : { key: 'monthly', dir: 'desc' }}
            rowActions={(s) => (
              <RowActions
                narrow={narrow}
                name={`${s.vendor} ${s.plan}`}
                actions={[
                  { label: `Edit ${s.vendor} ${s.plan}`, short: 'Edit subscription', icon: <Pencil />, onSelect: () => setSubDialog({ open: true, sub: s }) },
                  { label: `Delete ${s.vendor} ${s.plan}`, short: 'Delete subscription', icon: <Trash2 />, danger: true, onSelect: () => setConfirm({ kind: 'sub', s }) },
                ]}
              />
            )}
            empty={
              <EmptyState
                icon={<Repeat />}
                title="No seats or subscriptions"
                description="Add your Claude seats and the tools the team pays for."
                action={
                  <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setSubDialog({ open: true, sub: null })}>
                    Add subscription
                  </Button>
                }
              />
            }
          />
        </section>
        <Panel title="Node compute" sub={rated.length ? `${plural(rated.length, 'machine')} with an hourly rate` : 'No rates set'} icon={<Cpu />}>
          {rated.length ? (
            <ul className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-fg-muted">
              {rated.map((r) => (
                <li key={r.machineId}>
                  <span className="text-fg">{machines.find((m) => m.id === r.machineId)?.name ?? r.machineId}</span> at <span className="num">{formatUsd(r.hourlyRate)}</span> an hour online
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-2 text-sm text-fg-muted">
              <CircleAlert className="size-4 shrink-0 text-fg-subtle" aria-hidden />
              Owned machines cost nothing here until they have a rate.{' '}
              <Link to="/settings/finance" className="font-medium text-fg underline-offset-2 hover:underline">
                Set hourly rates
              </Link>
            </p>
          )}
        </Panel>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={RECEIPT_ACCEPT}
        className="sr-only"
        aria-label="Receipt file"
        onChange={(ev) => {
          const f = ev.target.files?.[0];
          ev.target.value = '';
          if (attachTo) void attach(attachTo, f);
        }}
      />
      <EntryDialog open={entryDialog.open} onOpenChange={(open) => setEntryDialog((d) => ({ ...d, open }))} entry={entryDialog.entry} />
      <SubscriptionDialog open={subDialog.open} onOpenChange={(open) => setSubDialog((d) => ({ ...d, open }))} sub={subDialog.sub} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.kind === 'entry' ? `Delete ${confirm.e.vendor} ${formatUsd(confirm.e.amount)}?` : confirm ? `Delete ${confirm.s.vendor} ${confirm.s.plan}?` : ''}
        description={
          confirm?.kind === 'entry'
            ? `It leaves every P&L and export it was in${confirm.e.receipt ? ', and its receipt is deleted from the hub' : ''}. This cannot be undone.`
            : 'Its cost leaves every period, past ones too. To stop it from today on, edit it and set a last day instead.'
        }
        confirmLabel={confirm?.kind === 'entry' ? 'Delete cost' : 'Delete subscription'}
        onConfirm={async () => {
          if (!confirm) return;
          if (confirm.kind === 'entry') await deleteEntry(confirm.e.id);
          else await deleteSubscription(confirm.s.id);
          setConfirm(null);
        }}
      />
    </>
  );
}
